import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Container, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Tooltip, Grid, Divider, Box, 
  CircularProgress, TextField, InputAdornment
} from '@material-ui/core';
import Autocomplete from "@material-ui/lab/Autocomplete";
import { 
  Delete as DeleteIcon, 
  AssignmentInd as AssignIcon, 
  Email as EmailIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon
} from '@material-ui/icons';

import api from '../../services/api';
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: '#2c3e50',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  tableContainer: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    borderRadius: '8px',
  },
  tableHeader: {
    backgroundColor: '#f4f6f9',
  },
  headerCell: {
    fontWeight: 'bold',
    color: '#34495e',
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
  },
  modalField: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  emailBody: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(2),
    borderRadius: '6px',
    border: '1px solid #e9ecef',
    maxHeight: '150px',
    overflowY: 'auto',
    whiteSpace: 'pre-line',
  }
}));

export default function TicketsReceived() {
  const classes = useStyles();

  // Estados dos Dados vindos do Banco
  const [novasSolicitacoes, setNovasSolicitacoes] = useState([]);
  const [users, setUsers] = useState([]);
  const [estruturaServicos, setEstruturaServicos] = useState([]);
  const [sistemasDisponiveis, setSistemasDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);

  // ESTADOS DOS FILTROS SUPERIORES
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Estados para busca dinâmica de contatos
  const [contactsOptions, setContactsOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadedContactsCache, setLoadedContactsCache] = useState([]);

  // Estados de Controle do Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Form de Aprovação para preencher TicketsCustom
  const [formApproval, setFormApproval] = useState({
    userId: "",
    contactId: "",
    servicoId: "",
    sistemaId: "",
    bonificacao: 0
  });

  // 1. CARREGAR DADOS DO BACKEND
  const fetchSolicitacoes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/emailsRecebidos');
      setNovasSolicitacoes(data || []);
    } catch (err) {
      console.error('Erro ao buscar e-mails:', err);
      toast.error("Erro ao buscar solicitações pendentes.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEstrutura = async () => {
    try {
      const { data } = await api.get("/custom-servicos");
      setEstruturaServicos(data || []);
    } catch (err) {
      console.error("Erro ao carregar serviços:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      if (data && data.users) {
        setUsers(data.users);
      } else if (Array.isArray(data)) {
        setUsers(data);
      }
    } catch (err) {
      console.error("Erro ao carregar usuários:", err);
    }
  };

  const fetchInitialContacts = async () => {
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: "", pageNumber: 1, limit: 100 }
      });
      const fetched = data.contacts || data || [];
      setLoadedContactsCache(Array.isArray(fetched) ? fetched : []);
    } catch (err) {
      console.error("Erro ao carregar contatos iniciais:", err);
    }
  };

  useEffect(() => {
    fetchSolicitacoes();
    fetchUsers();
    fetchEstrutura();
    fetchInitialContacts();
  }, []);

  // Lógica de Filtragem Local (Em Tempo Real)
  const solicitacoesFiltradas = useMemo(() => {
    return novasSolicitacoes.filter((item) => {
      // 1. Filtro de Texto (Busca no Título, Descrição, Nome e E-mail)
      const query = filterSearch.toLowerCase().trim();
      const matchSearch =
        !query ||
        (item.titulo && item.titulo.toLowerCase().includes(query)) ||
        (item.descricao && item.descricao.toLowerCase().includes(query)) ||
        (item.remetenteNome && item.remetenteNome.toLowerCase().includes(query)) ||
        (item.remetenteEmail && item.remetenteEmail.toLowerCase().includes(query));

      // 2. Filtro por Data
      const itemDate = new Date(item.createdAt);

      let matchStart = true;
      if (filterStartDate) {
        const startDate = new Date(`${filterStartDate}T00:00:00`);
        matchStart = itemDate >= startDate;
      }

      let matchEnd = true;
      if (filterEndDate) {
        const endDate = new Date(`${filterEndDate}T23:59:59`);
        matchEnd = itemDate <= endDate;
      }

      return matchSearch && matchStart && matchEnd;
    });
  }, [novasSolicitacoes, filterSearch, filterStartDate, filterEndDate]);

  // Função para limpar filtros
  const handleClearFilters = () => {
    setFilterSearch("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // Debounce para busca dinâmica de contatos
  useEffect(() => {
    if (!searchParam || searchParam.length < 3) {
      setContactsOptions([]);
      setLoadingContacts(false);
      return;
    }

    setLoadingContacts(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactsDynamic = async () => {
        try {
          const { data } = await api.get("/contacts", {
            params: { searchParam },
          });
          const fetchedContacts = data.contacts || data || [];
          setContactsOptions(Array.isArray(fetchedContacts) ? fetchedContacts : []);

          setLoadedContactsCache(prev => {
            const merged = [...prev];
            if (Array.isArray(fetchedContacts)) {
              fetchedContacts.forEach(c => {
                if (!merged.find(m => String(m.id) === String(c.id))) merged.push(c);
              });
            }
            return merged;
          });

          setLoadingContacts(false);
        } catch (err) {
          setLoadingContacts(false);
          console.error(err);
        }
      };

      fetchContactsDynamic();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  // Manipulação de Mudança no Serviço / Sistemas
  const handleServicoChange = (servicoId) => {
    const servico = estruturaServicos.find(s => Number(s.id) === Number(servicoId));
    setSistemasDisponiveis(servico ? servico.sistemas || [] : []);
    setFormApproval(prev => ({ ...prev, servicoId, sistemaId: "" }));
  };

  // Abertura do Modal de Atribuição e Aprovação
  const handleOpenModal = (ticket) => {
    setSelectedTicket(ticket);
    setSearchParam("");
    setSistemasDisponiveis([]);

    // Tentar localizar se o remetente do e-mail já existe na base de contatos
    const contactExistente = loadedContactsCache.find(c => 
      c.email === ticket.remetenteEmail || c.number === ticket.remetenteEmail
    );

    setFormApproval({
      userId: "",
      contactId: contactExistente ? contactExistente.id : "",
      servicoId: "",
      sistemaId: "",
      bonificacao: 0
    });

    if (contactExistente) {
      setContactsOptions([contactExistente]);
    }

    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTicket(null);
  };

  // Excluir solicitação
  const handleDeleteSolicitacao = async (id) => {
    if (window.confirm('Deseja realmente descartar esta solicitação?')) {
      try {
        await api.delete(`/emailsRecebidos/${id}`);
        toast.success("Solicitação removida.");
        fetchSolicitacoes();
      } catch (err) {
        console.error('Erro ao excluir solicitação:', err);
        toast.error("Erro ao excluir solicitação.");
      }
    }
  };

// Aprovação do e-mail e criação no TicketsCustom
  const handleAtribuirETicketsCustom = async () => {
    // 1. Trava para garantir que o Serviço e Sistema foram selecionados
    if (!formApproval.servicoId || !formApproval.sistemaId) {
      toast.warning('Selecione o Serviço e o Sistema para gerar o Ticket Custom.');
      return;
    }

    // 2. NOVA TRAVA: Garante que um Cliente/Contato foi selecionado no Autocomplete
    if (!formApproval.contactId) {
      toast.warning('Selecione o Cliente / Contato do Sistema antes de aprovar.');
      return;
    }

    try {
      await api.post(`/emailsRecebidos/${selectedTicket.id}/aprovar`, {
        userId: formApproval.userId ? Number(formApproval.userId) : null,
        contactId: Number(formApproval.contactId), // Envia obrigatoriamente o ID do cliente selecionado
        servicoId: Number(formApproval.servicoId),
        sistemaId: Number(formApproval.sistemaId),
        bonificacao: parseFloat(formApproval.bonificacao) || 0
      });

      toast.success(`Solicitação #${selectedTicket.id} aprovada e gravada em TicketsCustom!`);
      handleCloseModal();
      fetchSolicitacoes();
    } catch (err) {
      console.error('Erro ao aprovar solicitação:', err);
      toast.error('Erro ao aprovar e gravar o Ticket Custom.');
    }
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Typography variant="h5" component="h2" className={classes.sectionTitle}>
        <EmailIcon color="primary" /> Novas Solicitações de Implantação
      </Typography>

      {/* PAINEL DE FILTROS SUPERIOR */}
      <Paper className={classes.filterPaper}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Buscar por título, remetente ou conteúdo"
              variant="outlined"
              size="small"
              fullWidth
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Data Inicial"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Data Final"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={2} style={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              color="default"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!filterSearch && !filterStartDate && !filterEndDate}
              fullWidth
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* TABELA DE SOLICITAÇÕES */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table aria-label="tabela de novas solicitações">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.headerCell}>Título / Assunto</TableCell>
              <TableCell className={classes.headerCell}>Remetente</TableCell>
              <TableCell align="center" className={classes.headerCell}>Data</TableCell>
              <TableCell align="center" className={classes.headerCell}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 24 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : solicitacoesFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 24 }}>
                  <Typography variant="body1" color="textSecondary">
                    {novasSolicitacoes.length === 0
                      ? "Nenhuma nova solicitação pendente no momento."
                      : "Nenhuma solicitação atende aos filtros aplicados."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              solicitacoesFiltradas.map((item) => (
                <TableRow key={item.id} className={classes.row}>
                  <TableCell component="th" scope="row">
                    <Typography style={{ fontWeight: 600 }}>{item.titulo}</Typography>
                    <Typography variant="caption" color="textSecondary" noWrap style={{ maxWidth: 350, display: 'block' }}>
                      {item.descricao}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.remetenteNome || '-'}</Typography>
                    <Typography variant="caption" color="textSecondary">{item.remetenteEmail}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {new Date(item.createdAt).toLocaleString('pt-BR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Aprovar e Criar Ticket Custom">
                      <IconButton color="primary" onClick={() => handleOpenModal(item)}>
                        <AssignIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir / Descartar">
                      <IconButton color="secondary" onClick={() => handleDeleteSolicitacao(item.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODAL DE APROVAÇÃO */}
      {selectedTicket && (
        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle style={{ fontWeight: 'bold' }}>
            Aprovar Solicitação #{selectedTicket.id} & Preencher Ticket Custom
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Assunto:</Typography>
                <Typography variant="h6">{selectedTicket.titulo}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Solicitante:</Typography>
                <Typography variant="body1">{selectedTicket.remetenteNome || '-'}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">E-mail:</Typography>
                <Typography variant="body1">{selectedTicket.remetenteEmail}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Descrição Recebida:</Typography>
                  <Paper className={classes.emailBody} elevation={0}>
                    <Typography variant="body2">{selectedTicket.descricao}</Typography>
                  </Paper>
                </Box>
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 12 }}>
              Dados Iniciais do Ticket Custom:
            </Typography>

            <Grid container spacing={2}>
              {/* VINCULAR OU BUSCAR CONTATO */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={contactsOptions}
                  loading={loadingContacts}
                  getOptionLabel={(option) => option ? option.name || "" : ""}
                  value={loadedContactsCache.find(c => String(c.id) === String(formApproval.contactId)) || null}
                  onChange={(event, newValue) => {
                    setFormApproval({ ...formApproval, contactId: newValue ? newValue.id : "" });
                  }}
                  onInputChange={(e, value) => setSearchParam(value)}
                  getOptionSelected={(option, value) => String(option.id) === String(value.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente / Contato do Sistema"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.modalField}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* SELECIONAR ANALISTA (USER) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Analista Responsável"
                  variant="outlined"
                  fullWidth
                  size="small"
                  className={classes.modalField}
                  value={formApproval.userId}
                  onChange={e => setFormApproval({ ...formApproval, userId: e.target.value })}
                >
                  <MenuItem value=""><em>Nenhum / Pendente</em></MenuItem>
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* TIPO DE SERVIÇO */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo de Serviço"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  className={classes.modalField}
                  value={formApproval.servicoId}
                  onChange={e => handleServicoChange(e.target.value)}
                >
                  {estruturaServicos.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* SISTEMA */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Sistema"
                  variant="outlined"
                  fullWidth
                  required
                  disabled={sistemasDisponiveis.length === 0}
                  size="small"
                  className={classes.modalField}
                  value={formApproval.sistemaId}
                  onChange={e => setFormApproval({ ...formApproval, sistemaId: e.target.value })}
                >
                  {sistemasDisponiveis.map(sys => (
                    <MenuItem key={sys.id} value={sys.id}>{sys.nome}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* BONIFICAÇÃO INICIAL */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bonificação Inicial (R$)"
                  variant="outlined"
                  type="number"
                  fullWidth
                  size="small"
                  className={classes.modalField}
                  value={formApproval.bonificacao}
                  onChange={e => setFormApproval({ ...formApproval, bonificacao: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions style={{ padding: 16 }}>
            <Button onClick={handleCloseModal} color="default">
              Cancelar
            </Button>
            <Button 
              onClick={handleAtribuirETicketsCustom} 
              color="primary" 
              variant="contained" 
              disabled={!formApproval.servicoId || !formApproval.sistemaId}
            >
              Criar Ticket Custom & Aprovar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}